/** Pure helpers for the Compare page (A6). */

import { formatVersionDate, labelPrefixForRegion } from './labelVersionFormat';
import { resolveForRegion, type ConceptEntry } from './universalSections';

export const PANEL_KIND_REGIONS = ['US', 'US_FDA', 'EU', 'UK', 'CA', 'AU', 'CN'] as const;

export function panelVersionLine(
  region: string | null | undefined,
  versionNumber: number | null | undefined,
  effectiveTime: string | null | undefined,
  formatTag: 'Text' | 'PDF' | null,
): string {
  const parts: string[] = [];
  const r = (region || '').toUpperCase();
  if ((PANEL_KIND_REGIONS as readonly string[]).includes(r)) parts.push(labelPrefixForRegion(r));
  if (versionNumber != null) parts.push(`v${versionNumber}`);
  const date = formatVersionDate(effectiveTime);
  if (date) parts.push(date);
  if (formatTag != null) parts.push(formatTag);
  return parts.join(' · ');
}

export function railCount(n: number, hasMore: boolean): string {
  return `${n}${hasMore ? '+' : ''}`;
}

export function compareBackLabel(isCogni: boolean, count: string | null): string {
  if (count == null || count === '0') return '← Back';
  return `← ${count} ${isCogni ? 'Cogni' : 'keyword'} results`;
}

export type FilterMeta = {
  brand_name?: string | null;
  generic_name?: string | null;
  manufacturer?: string | null;
} | null;

export function matchesRailFilter(meta: FilterMeta, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q || !meta) return true;
  return [meta.brand_name, meta.generic_name, meta.manufacturer].some(
    (f) => (f ?? '').toLowerCase().includes(q),
  );
}

/** Section block for a jump: exact `data-section` wins, else the first dotted
 *  descendant (never a bare prefix: "5" must not hit "51"). Anchors are
 *  cross-reference links, not blocks. */
export function findSectionBlock(root: ParentNode, scrollTo: string): HTMLElement | null {
  const blocks = Array.from(root.querySelectorAll<HTMLElement>('[data-section]:not(a)'));
  return (
    blocks.find((b) => b.getAttribute('data-section') === scrollTo) ??
    blocks.find((b) => (b.getAttribute('data-section') ?? '').startsWith(scrollTo + '.')) ??
    null
  );
}

export type TopicEntry = { label: string; text: string; jumpTo: string | null };

export function topicEntries(
  picks: ConceptEntry[],
  region: string,
  sections: { section_number: string | null; title: string | null }[],
): TopicEntry[] {
  const ref = (num: string): string => {
    const title = (sections.find((s) => s.section_number === num)?.title ?? '')
      .trim()
      .replace(/^\d+(\.\d+)*\s*[.:-]?\s*/, '');
    return `${region === 'US' ? '§' : ''}${num}${title ? ` ${title}` : ''}`;
  };
  return picks
    .filter((p) => p.universal_order != null)
    .map((p) => {
      const r = resolveForRegion(p, region);
      if (r.notApplicable) return { label: p.label, text: `Not a section in ${region}`, jumpTo: null };
      if (r.note) return { label: p.label, text: r.note, jumpTo: r.scrollTo };
      return { label: p.label, text: r.scrollTo ? ref(r.scrollTo) : '', jumpTo: r.scrollTo };
    });
}
